import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { useCallback, useEffect } from 'react'
import { InfiniteData } from 'react-query'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useQueryClient } from 'hooks/useQueryClient'

import { db } from 'utils/firebase'

import { Room } from 'types/chat'

export const useLiveChatQuery = () => {
  const { data: currentUser } = useCurrentUser()
  const queryClient = useQueryClient()

  const getUpdatedRoomData = useCallback(
    (oldRoom: Room, newRoom: Room) => {
      if (!currentUser) return oldRoom

      const hasNewMessage =
        newRoom.lastMessage?.created &&
        newRoom.lastMessage?.creator.id !== currentUser.id &&
        newRoom.lastMessage?.created > (oldRoom.lastMessage?.created || 0)

      const oldMessageCount = oldRoom.meta?.newMessageCount || 0
      const newMessageCount = hasNewMessage ? oldMessageCount + 1 : oldMessageCount

      return {
        ...newRoom,
        meta: {
          ...oldRoom.meta,
          newMessageCount,
        },
      }
    },
    [currentUser],
  )

  useEffect(() => {
    if (!currentUser) return () => {}

    const roomQuery = query(
      collection(db, 'rooms'),
      where('reference.ids', 'array-contains', currentUser.id),
    )

    const unsubscribe = onSnapshot(roomQuery, (snapshot) => {
      if (!currentUser) {
        return
      }

      const queryClientRoomData: InfiniteData<Room[]> = queryClient.getQueryData([
        'rooms',
        currentUser.id,
      ]) || {
        pageParams: [],
        pages: [],
      }

      const updatedRoomData = {
        ...queryClientRoomData,
        pages: [...queryClientRoomData.pages],
      }

      snapshot.docChanges().forEach((change) => {
        const changedRoom = {
          ...change.doc.data(),
          id: change.doc.id,
        } as Room

        let roomFound = false

        updatedRoomData.pages = updatedRoomData.pages.map((roomPage) =>
          roomPage.map((room) => {
            if (room.id === changedRoom.id) {
              roomFound = true
              return getUpdatedRoomData(room, changedRoom)
            }

            return room
          }),
        )

        roomFound
          ? queryClient.setQueryData(['rooms', currentUser.id], updatedRoomData)
          : queryClient.invalidateQueries(['rooms', currentUser.id])
      })
    })

    return unsubscribe
  }, [currentUser, currentUser?.id, getUpdatedRoomData, queryClient])
}
